import React, { useState, useEffect } from 'react';
import { analyticsAPI, loyaltyAPI } from '../services/api';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { Users, ShoppingCart, TrendingUp, AlertTriangle, DollarSign, Repeat, Sparkles, Loader } from 'lucide-react';

const DEFAULT_TIER_COLORS = {
  bronze: '#b87333',
  silver: '#94a3b8',
  gold: '#facc15',
  platinum: '#a78bfa',
};

const tooltipStyle = {
  backgroundColor: '#1a2035',
  border: '1px solid #1e293b',
  borderRadius: 8,
  fontSize: 12,
  color: '#f0f2f5',
};
const tooltipLabelStyle = { color: '#f0f2f5' };
const tooltipItemStyle = { color: '#8b95a8' };

function renderMarkdown(text) {
  if (!text) return null;
  return text.split('\n').map((line, i) => {

    let html = line.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

    html = html.replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>');

    if (html.trim() === '') return <br key={i} />;
    return <p key={i} style={{ margin: '4px 0' }} dangerouslySetInnerHTML={{ __html: html }} />;
  });
}

export default function DashboardPage() {
  const [metrics, setMetrics] = useState(null);
  const [tierConfigs, setTierConfigs] = useState([]);
  const [insights, setInsights] = useState('');
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      analyticsAPI.dashboard(),
      loyaltyAPI.tiers(),
    ]).then(([dashRes, tiersRes]) => {
      setMetrics(dashRes.data);
      setTierConfigs(tiersRes.data);
    }).catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const loadInsights = () => {
    setInsightsLoading(true);
    analyticsAPI.insights()
      .then((res) => setInsights(res.data.insights))
      .catch(() => setInsights('Ошибка загрузки рекомендаций'))
      .finally(() => setInsightsLoading(false));
  };

  if (loading) return <div style={{ color: 'var(--text-muted)', padding: 40 }}>Загрузка аналитики...</div>;
  if (!metrics) return <div style={{ color: 'var(--text-muted)', padding: 40 }}>Нет данных</div>;

  const tierColorMap = {};
  const tierNameMap = {};
  tierConfigs.forEach(t => { tierColorMap[t.name] = t.color; tierNameMap[t.name] = t.display_name; });

  const tierData = Object.entries(metrics.tier_distribution).map(([name, value]) => ({
    name: tierNameMap[name] || name,
    value,
    color: tierColorMap[name] || DEFAULT_TIER_COLORS[name] || '#6366f1',
  }));

  return (
    <div className="animate-in">
      <div className="page-header">
        <h1 className="page-title">Дашборд</h1>
        <p className="page-subtitle">Ключевые метрики бизнеса</p>
      </div>

      <div className="card-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}>
        <div className="stat-card">
          <div className="stat-card-label"><Users size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />Клиенты</div>
          <div className="stat-card-value">{metrics.total_clients}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-label"><ShoppingCart size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />Заказы</div>
          <div className="stat-card-value">{metrics.total_orders}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-label"><DollarSign size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />Выручка</div>
          <div className="stat-card-value">{metrics.total_revenue.toLocaleString('ru')} ₽</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-label"><TrendingUp size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />Средний чек</div>
          <div className="stat-card-value">{metrics.avg_check.toLocaleString('ru')} ₽</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-label"><Repeat size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />Частота</div>
          <div className="stat-card-value">{metrics.avg_frequency}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-label"><AlertTriangle size={14} style={{ verticalAlign: 'middle', marginRight: 6, color: 'var(--danger)' }} />Отток</div>
          <div className="stat-card-value" style={{ color: 'var(--danger)' }}>{metrics.churn_count} ({metrics.churn_rate}%)</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginTop: 24 }}>
        <div className="card">
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: 'var(--text-secondary)' }}>Выручка по месяцам</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={metrics.revenue_by_month}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="month" tick={{ fill: '#8b95a8', fontSize: 11 }} />
              <YAxis tick={{ fill: '#8b95a8', fontSize: 11 }} />
              <Tooltip contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} itemStyle={tooltipItemStyle} />
              <Bar dataKey="value" fill="#6366f1" radius={[4, 4, 0, 0]} name="Выручка, ₽" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="card">
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: 'var(--text-secondary)' }}>Заказы по месяцам</h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={metrics.orders_by_month}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="month" tick={{ fill: '#8b95a8', fontSize: 11 }} />
              <YAxis tick={{ fill: '#8b95a8', fontSize: 11 }} />
              <Tooltip contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} itemStyle={tooltipItemStyle} />
              <Line type="monotone" dataKey="value" stroke="#10b981" strokeWidth={2} dot={{ r: 4 }} name="Заказов" />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="card">
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: 'var(--text-secondary)' }}>Уровни лояльности</h3>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={tierData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                innerRadius={60} outerRadius={100} paddingAngle={3}>
                {tierData.map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} itemStyle={tooltipItemStyle} />
              <Legend wrapperStyle={{ fontSize: 12, color: '#8b95a8' }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="card">
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: 'var(--text-secondary)' }}>Новые клиенты по месяцам</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={metrics.new_clients_by_month}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="month" tick={{ fill: '#8b95a8', fontSize: 11 }} />
              <YAxis tick={{ fill: '#8b95a8', fontSize: 11 }} />
              <Tooltip contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} itemStyle={tooltipItemStyle} />
              <Bar dataKey="value" fill="#f59e0b" radius={[4, 4, 0, 0]} name="Клиентов" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>


      <div className="card" style={{ marginTop: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: insights ? 16 : 0 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)', margin: 0 }}>
            <Sparkles size={16} style={{ verticalAlign: 'middle', marginRight: 8, color: 'var(--accent)' }} />
            AI-аналитика
          </h3>
          <button className="btn btn-primary btn-sm" onClick={loadInsights} disabled={insightsLoading}>
            {insightsLoading ? <><Loader size={14} className="spin" /> Анализ...</> : <><Sparkles size={14} /> Получить рекомендации</>}
          </button>
        </div>
        {insights && (
          <div style={{ fontSize: 13, lineHeight: 1.8, color: 'var(--text-primary)' }}>
            {renderMarkdown(insights)}
          </div>
        )}
        {!insights && !insightsLoading && (
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 12 }}>
            Нажмите кнопку для получения AI-рекомендаций на основе ваших данных
          </p>
        )}
      </div>
    </div>
  );
}